import FormattedDate from "@/components/FormattedDate"
import { EnvelopeIcon } from "@/components/Icons"
import { SheetSection } from "@/components/Sections/SheetSection"
import { P2, P3, P4 } from "@/components/Typographies"
import { getDisplayedName } from "@/domain/collaborator/getDisplayedName"
import { NIL_UUID } from "@/domain/resources/constants"
import { useGenericErrorToast } from "@/hooks/useGenericErrorToast"
import {
  type CommentJsonldReadCollectionSharedData,
  type SharedDataJsonldReadCollectionSharedData,
  usePatchInterbu,
} from "@/networking/api"
import { isNotNullNorUndefined, isNullOrUndefined } from "@/utils/ts-utils"
import { Box, Button, Flex, Spinner, Textarea } from "@chakra-ui/react"
import { useMemo, useRef, useState } from "react"
import { FormattedMessage, useIntl } from "react-intl"

type Props = {
  entity: SharedDataJsonldReadCollectionSharedData
  refreshQueries?: () => void
  // Write a new comment; the thread itself is shown to both sides (ADR 0009, D4).
  canWrite?: boolean
}

export const CommentSection = ({ entity, refreshQueries, canWrite = true }: Props) => {
  const intl = useIntl()
  const appToast = useGenericErrorToast()
  const ref = useRef<HTMLTextAreaElement>(null)

  const comments = useMemo(
    () =>
      (entity.interBu?.comments ?? [])
        .filter(c => isNotNullNorUndefined(c.comment))
        .map(c => c.comment as CommentJsonldReadCollectionSharedData),
    [entity]
  )

  // const {} = useGetInterBu(entity.interBu?.comments)
  const { mutateAsync: patchInterBu, isPending } = usePatchInterbu()
  const [isSaving, setIsSaving] = useState<boolean>(false)
  const isLoading = useMemo<boolean>(
    () => isSaving === true || isPending === true,
    [isSaving, isPending]
  )

  const onAddMessage = () => {
    const field = ref.current
    if (isNullOrUndefined(field)) return

    const v = field.value

    if (v.replace(/[^\W]/, "").length > 1) {
      setIsSaving(true)
      patchInterBu(
        {
          id: entity.interBu?.id ?? NIL_UUID,
          data: {
            comment: v,
          },
        },
        {
          onError: () => {
            setIsSaving(false)
            appToast()
          },
          onSuccess: () => {
            setIsSaving(false)
            refreshQueries?.()
          },
        }
      )
    }

    field.value = ""
    ref.current?.blur()
  }

  // A block per message (author, date, text), then the input on the whole width (ADR 0009, D4).
  return (
    <SheetSection
      title={intl.formatMessage({ id: "userSharedData.comments_thread" })}
      icon={EnvelopeIcon}
      count={comments.length}
    >
      <Flex direction="column" gap="5">
        {comments.length > 0 && (
          <Flex
            as="ol"
            listStyleType="none"
            direction="column"
            gap="4"
            data-testid="comment-thread"
          >
            {comments.map((comment, idx) => (
              <Box as="li" key={`${idx}_${comment?.createdAt}`}>
                <Flex gap="2" alignItems="baseline" wrap="wrap">
                  <P3 fontWeight="semibold">{getDisplayedName(comment?.fromUser)}</P3>
                  <P4 color="textMuted">
                    <FormattedDate
                      date={comment.createdAt}
                      includeTime={true}
                      options={{ dateStyle: "short", timeStyle: "short" }}
                    />
                  </P4>
                </Flex>
                <Box mt="1" px="4" py="3" bg="surfaceAlt" borderLeft="2px solid" borderColor="line">
                  <P2 display="block" whiteSpace="pre-wrap">
                    {comment?.message}
                  </P2>
                </Box>
              </Box>
            ))}
          </Flex>
        )}
        {comments.length === 0 && !canWrite && (
          <P3 color="textMuted">
            <FormattedMessage id="userSharedData.comments_thread.none" />
          </P3>
        )}
        {canWrite &&
          (isLoading ? (
            <Spinner />
          ) : (
            <Flex direction="column" gap="2">
              <Textarea
                ref={ref}
                variant="outlineWithBorder"
                aria-label={intl.formatMessage({
                  id: "userSharedData.comments_thread.new_comment",
                })}
                placeholder={intl.formatMessage({
                  id: "userSharedData.comments_thread.new_comment",
                })}
              />
              <Flex justifyContent="flex-end">
                <Button size="sm" variant="solid" onClick={() => onAddMessage()}>
                  <FormattedMessage id="userSharedData.comments_thread.none.btn_add" />
                </Button>
              </Flex>
            </Flex>
          ))}
      </Flex>
    </SheetSection>
  )
}
